"use client";

import { useState } from "react";
import { 
  Target, 
  Eye, 
  Users, 
  Award,
  ShieldCheck,
  Zap,
  Heart,
  TrendingUp
} from "lucide-react";
import Wrapper from "@/layout/wrapper";
import Header from "@/layout/headers/header";
import Footer from "@/layout/footers/footer";

const AboutUs = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const values = [
    {
      id: 1,
      icon: ShieldCheck,
      title: "Quality Assurance",
      description: "Authentic products guaranteed",
      color: "#00A859",
      bgGradient: "linear-gradient(135deg, #00A859 0%, #00C766 100%)"
    },
    {
      id: 2,
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction is priority",
      color: "#E91E63",
      bgGradient: "linear-gradient(135deg, #E91E63 0%, #F06292 100%)"
    },
    {
      id: 3,
      icon: Zap,
      title: "Fast Service",
      description: "Quick delivery guaranteed",
      color: "#FF9800",
      bgGradient: "linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)"
    },
    {
      id: 4,
      icon: Award,
      title: "Best Prices",
      description: "Competitive rates always",
      color: "#9C27B0",
      bgGradient: "linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)"
    }
  ];

  return (
    <Wrapper>
      <Header></Header>
    <section style={{ 
      padding: "clamp(60px, 10vw, 100px) clamp(15px, 3vw, 20px)", 
      background: "#ffffff",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Background Pattern */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        pointerEvents: "none"
      }} />

      <div style={{ maxWidth: "1400px", margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "70px", padding: "0 15px" }}>
          <div style={{
            display: "inline-block",
            background: "linear-gradient(135deg, #FF6B35 0%, #f7931e 100%)",
            padding: "8px 24px",
            borderRadius: "30px",
            marginBottom: "20px"
          }}>
            <span style={{
              color: "#fff",
              fontSize: "14px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              fontFamily: "'Poppins', sans-serif"
            }}>About iMall</span>
          </div>
          
          <h2 style={{ 
            fontSize: "clamp(32px, 6vw, 48px)", 
            fontWeight: "900", 
            color: "#1a1a1a",
            margin: "0 0 20px 0",
            fontFamily: "'Poppins', sans-serif",
            lineHeight: "1.2"
          }}>
            Your Trusted Shopping Partner
          </h2>
          <p style={{ 
            fontSize: "clamp(16px, 3vw, 20px)", 
            color: "#666", 
            maxWidth: "700px", 
            margin: "0 auto",
            lineHeight: "1.7",
            fontFamily: "'Poppins', sans-serif"
          }}>
            Bringing thousands of products from top brands to your doorstep with quality, trust, and convenience
          </p>
        </div>

        {/* Story Section */}
        <div style={{
          background: "linear-gradient(135deg, #FF6B35 0%, #f7931e 100%)",
          borderRadius: "24px",
          padding: "clamp(40px, 6vw, 60px)",
          marginBottom: "60px",
          color: "#fff",
          boxShadow: "0 20px 60px rgba(255, 107, 53, 0.3)",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Decorative circles */}
          <div style={{
            position: "absolute",
            top: "-80px",
            right: "-80px",
            width: "200px",
            height: "200px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            filter: "blur(40px)"
          }} />
          <div style={{
            position: "absolute",
            bottom: "-60px",
            left: "-60px",
            width: "180px",
            height: "180px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            filter: "blur(40px)"
          }} />
          
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "40px",
              alignItems: "center"
            }}>
              <div>
                <div style={{
                  width: "70px",
                  height: "70px",
                  background: "rgba(255, 255, 255, 0.25)",
                  borderRadius: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "25px"
                }}>
                  <TrendingUp size={38} color="#fff" strokeWidth={2.5} />
                </div>
                <h3 style={{
                  fontSize: "clamp(24px, 4vw, 32px)",
                  fontWeight: "800",
                  marginBottom: "15px",
                  fontFamily: "'Poppins', sans-serif",
                  color: "#fff"
                }}>
                  Our Story
                </h3>
                <p style={{
                  fontSize: "clamp(15px, 2.5vw, 17px)",
                  lineHeight: "1.8",
                  fontFamily: "'Poppins', sans-serif",
                  color: "#fff"
                }}>
                  iMall started with a simple mission: to make quality products accessible to everyone. We've grown into a trusted platform connecting thousands of customers with their favorite brands every day.
                </p>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "clamp(24px, 4vw, 32px)",
                  fontWeight: "800",
                  marginBottom: "15px",
                  fontFamily: "'Poppins', sans-serif",
                  color: "#fff"
                }}>
                  What We Do
                </h3>
                <p style={{
                  fontSize: "clamp(15px, 2.5vw, 17px)",
                  lineHeight: "1.8",
                  fontFamily: "'Poppins', sans-serif",
                  marginBottom: "20px",
                  color: "#fff"
                }}>
                  We curate products from electronics to fashion, beauty to home essentials - all from authorized sellers. Every item is verified for authenticity, ensuring you get exactly what you expect.
                </p>
                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  <div style={{
                    background: "rgba(255, 255, 255, 0.25)",
                    padding: "10px 20px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#030303"
                  }}>🎯 100% Authentic</div>
                  <div style={{
                    background: "rgba(255, 255, 255, 0.25)",
                    padding: "10px 20px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#000000"
                  }}>🚀 Fast Delivery</div>
                  <div style={{
                    background: "rgba(255, 255, 255, 0.25)",
                    padding: "10px 20px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#030303"
                  }}>💳 Secure Payment</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div 
          className="mission-vision-grid"
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "clamp(20px, 3vw, 30px)",
            marginBottom: "70px",
            padding: "0 15px"
          }}>
          {/* Mission */}
          <div style={{
            background: "#fff",
            padding: "clamp(35px, 5vw, 45px)",
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
            border: "2px solid #f5f5f5",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-8px)";
            e.currentTarget.style.boxShadow = "0 15px 50px rgba(33, 150, 243, 0.15)";
            e.currentTarget.style.borderColor = "#2196F3";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 40px rgba(0, 0, 0, 0.08)";
            e.currentTarget.style.borderColor = "#f5f5f5";
          }}>
            <div style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "150px",
              height: "150px",
              background: "linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, transparent 100%)",
              borderRadius: "0 0 0 100%"
            }} />
            
            <div style={{
              width: "65px",
              height: "65px",
              background: "linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "25px",
              boxShadow: "0 8px 24px rgba(33, 150, 243, 0.3)"
            }}>
              <Target size={32} color="#fff" strokeWidth={2.5} />
            </div>
            <h3 style={{
              fontSize: "clamp(22px, 3.5vw, 28px)",
              fontWeight: "800",
              color: "#1a1a1a",
              marginBottom: "15px",
              fontFamily: "'Poppins', sans-serif"
            }}>
              Our Mission
            </h3>
            <p style={{
              fontSize: "clamp(14px, 2.5vw, 16px)",
              color: "#666",
              lineHeight: "1.8",
              fontFamily: "'Poppins', sans-serif",
              margin: 0
            }}>
              To provide customers with seamless access to quality products from trusted brands at competitive prices, ensuring a safe and convenient shopping experience with fast, reliable delivery.
            </p>
          </div>

          {/* Vision */}
          <div style={{
            background: "#fff",
            padding: "clamp(35px, 5vw, 45px)",
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
            border: "2px solid #f5f5f5",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-8px)";
            e.currentTarget.style.boxShadow = "0 15px 50px rgba(156, 39, 176, 0.15)";
            e.currentTarget.style.borderColor = "#9C27B0";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 40px rgba(0, 0, 0, 0.08)";
            e.currentTarget.style.borderColor = "#f5f5f5";
          }}>
            <div style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "150px",
              height: "150px",
              background: "linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, transparent 100%)",
              borderRadius: "0 0 0 100%"
            }} />
            
            <div style={{
              width: "65px",
              height: "65px",
              background: "linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "25px",
              boxShadow: "0 8px 24px rgba(156, 39, 176, 0.3)"
            }}>
              <Eye size={32} color="#fff" strokeWidth={2.5} />
            </div>
            <h3 style={{
              fontSize: "clamp(22px, 3.5vw, 28px)",
              fontWeight: "800",
              color: "#1a1a1a",
              marginBottom: "15px",
              fontFamily: "'Poppins', sans-serif"
            }}>
              Our Vision
            </h3>
            <p style={{
              fontSize: "clamp(14px, 2.5vw, 16px)",
              color: "#666",
              lineHeight: "1.8",
              fontFamily: "'Poppins', sans-serif",
              margin: 0
            }}>
              To become the most trusted and preferred online shopping destination, revolutionizing e-commerce by creating meaningful connections between customers and their favorite brands.
            </p>
          </div>
        </div>

        {/* Our Values */}
        <div style={{ padding: "0 15px" }}>
          <div style={{ textAlign: "center", marginBottom: "50px" }}>
            <h3 style={{
              fontSize: "clamp(28px, 5vw, 38px)",
              fontWeight: "900",
              color: "#1a1a1a",
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "12px"
            }}>
              Our Core Values
            </h3>
            <p style={{
              fontSize: "clamp(15px, 2.5vw, 17px)",
              color: "#666",
              fontFamily: "'Poppins', sans-serif"
            }}>
              The principles that guide everything we do
            </p>
          </div>

          <div 
            className="values-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "clamp(15px, 3vw, 25px)"
            }}>
            {values.map((value) => {
              const IconComponent = value.icon;
              const isActive = hoveredCard === value.id;
              
              return (
                <div
                  key={value.id}
                  onMouseEnter={() => setHoveredCard(value.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: "#fff",
                    borderRadius: "18px",
                    padding: "30px 24px",
                    boxShadow: isActive 
                      ? `0 15px 45px ${value.color}30`
                      : "0 8px 30px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: isActive ? "translateY(-12px)" : "translateY(0)",
                    border: isActive ? `2px solid ${value.color}` : "2px solid transparent",
                    cursor: "pointer",
                    textAlign: "center"
                  }}
                >
                  <div style={{
                    width: "68px",
                    height: "68px",
                    background: isActive ? value.bgGradient : `${value.color}15`,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 18px",
                    transition: "all 0.4s ease",
                    boxShadow: isActive ? `0 8px 24px ${value.color}40` : "none"
                  }}>
                    <IconComponent 
                      size={30} 
                      color={isActive ? "#fff" : value.color}
                      strokeWidth={2.5}
                    />
                  </div>

                  <h4 style={{
                    fontSize: "clamp(16px, 2.8vw, 19px)",
                    fontWeight: "700",
                    color: "#1a1a1a",
                    marginBottom: "10px",
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    {value.title}
                  </h4>

                  <p style={{
                    fontSize: "clamp(13px, 2.2vw, 14px)",
                    color: "#666",
                    lineHeight: "1.5",
                    fontFamily: "'Poppins', sans-serif",
                    margin: 0
                  }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Responsive CSS */}
        <style jsx>{`
          @media (max-width: 767px) {
            .mission-vision-grid {
              grid-template-columns: repeat(1, 1fr) !important;
              gap: 20px !important;
            }
            .values-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
          }
          @media (max-width: 480px) {
            .values-grid {
              gap: 10px !important;
            }
          }
          @media (min-width: 768px) and (max-width: 1023px) {
            .mission-vision-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .values-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
        `}</style>
      </div>
    
    </section>
    <Footer></Footer>
      </Wrapper>
  );
};

export default AboutUs;